import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function AgeCalculator() {
  const { t } = useTranslation();
  const [birthDate, setBirthDate] = useState("");
  const [results, setResults] = useState<any>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: 'https://calclab.app' },
      { name: t('categories.lifestyle'), url: 'https://calclab.app/lifestyle' },
      { name: "Age Calculator", url: "https://calclab.app/calculator/age" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Age Calculator",
      "Calculate your exact age in years, months, days, weeks, and hours. Enter your birth date to find detailed age information and days until next birthday.",
      "https://calclab.app/calculator/age",
      lang
    )
  ]);

  const calculateAge = () => {
    if (!birthDate) return;
    
    const birth = new Date(birthDate);
    const now = new Date();
    
    let years = now.getFullYear() - birth.getFullYear();
    let months = now.getMonth() - birth.getMonth();
    let days = now.getDate() - birth.getDate();
    
    if (days < 0) {
      months--;
      days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    }
    
    if (months < 0) {
      years--;
      months += 12;
    }
    
    const totalDays = Math.floor((now.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24));
    const totalWeeks = Math.floor(totalDays / 7);
    const totalMonths = years * 12 + months;
    const totalHours = totalDays * 24;
    
    setResults({
      years,
      months,
      days,
      totalDays,
      totalWeeks,
      totalMonths,
      totalHours,
      nextBirthday: getNextBirthday(birth)
    });
  };

  const getNextBirthday = (birth: Date) => {
    const now = new Date();
    const nextBday = new Date(now.getFullYear(), birth.getMonth(), birth.getDate());
    if (nextBday < now) {
      nextBday.setFullYear(now.getFullYear() + 1);
    }
    const daysUntil = Math.ceil((nextBday.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntil;
  };

  return (
    <PageLayout calculatorName="Age Calculator" calculatorPath="/calculator/age">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Calculate Your Age</CardTitle>
            <CardDescription>Enter your date of birth</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Date of Birth</Label>
              <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
            </div>
            <Button onClick={calculateAge} className="w-full">Calculate Age</Button>
          </CardContent>
        </Card>

        {results && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Your Age</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center mb-4">
                  <div className="text-5xl font-bold text-primary">{results.years}</div>
                  <div className="text-lg text-muted-foreground">years old</div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Exact Age:</span>
                    <span className="font-semibold">{results.years}y {results.months}m {results.days}d</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Months:</span>
                    <span>{results.totalMonths.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Weeks:</span>
                    <span>{results.totalWeeks.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Days:</span>
                    <span>{results.totalDays.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Hours:</span>
                    <span>{results.totalHours.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <span>Next Birthday:</span>
                    <span className="font-semibold text-primary">{results.nextBirthday} days</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <CalculatorSuperCalcCTA config={calculatorCTAs["age"]} trigger={results.totalDays} />
          </>
        )}
      </div>
    </PageLayout>
  );
}
